/**
 * SearchField — pill-shaped site-search control from the storefront reference.
 *
 * Ported from `resources/Dental supply storefront design system/tw-components.jsx`.
 * Mechanical deviations only: `cx` and `Icon` are imported, the input disables
 * browser autocomplete, and the submit callback receives the current value. One
 * addition: `aria-label` names the input (upstream hard-codes "Search products"), and an
 * icon-only submit button (`buttonLabel=""`) is named "Search" and drops its empty label
 * span, whose `gap` would otherwise push the icon off-centre. The leading magnifier is
 * `text-ink` (near-black), not upstream's grey `text-ink-3` → `text-ink-2` on focus.
 */
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";

export function SearchField({
  value = "",
  onChange,
  onSubmit,
  placeholder = "Search 30,000+ products, brands & SKUs…",
  showButton = true,
  buttonLabel = "Search",
  "aria-label": ariaLabel = "Search products",
  style = {},
  className = "",
}) {
  return (
    <form
      role="search"
      autoComplete="off"
      style={style}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit?.(value);
      }}
      className={cx(
        "dnd-search group/sf flex h-[50px] w-full items-center gap-[var(--space-2)] rounded-[var(--radius-pill)] border-[1.5px] border-line bg-card pl-[var(--space-5)] pr-[6px]",
        "[transition:border-color_var(--duration-fast),box-shadow_var(--duration-base)_var(--ease-out)] focus-within:border-line-strong focus-within:shadow-md",
        className
      )}
    >
      <Icon className="text-ink" name="search" size={22} weight={400} />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(event) => onChange?.(event.target.value)}
        className="dnd-search__input h-full min-w-0 flex-1 border-0 bg-transparent font-sans text-base text-ink outline-none placeholder:text-ink-3 [&::-webkit-search-cancel-button]:appearance-none"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange?.("")}
          className="inline-flex cursor-pointer rounded-full border-0 bg-transparent p-[6px] text-ink-3 [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        >
          <Icon name="close" size={18} weight={500} />
        </button>
      )}
      {showButton && (
        <button
          type="submit"
          aria-label={buttonLabel ? undefined : "Search"}
          className="dnd-search__go inline-flex h-[38px] cursor-pointer items-center gap-[6px] rounded-[var(--radius-pill)] border-0 bg-orange-500 px-[var(--space-5)] font-sans text-sm font-semibold text-white [transition:background_var(--duration-fast),transform_var(--duration-base)_var(--ease-spring)] hover:bg-orange-600 active:scale-[0.95] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        >
          <Icon name="search" size={18} weight={500} />
          {buttonLabel && (
            <span className="hidden sm:inline">{buttonLabel}</span>
          )}
        </button>
      )}
    </form>
  );
}
